import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  BxBadgeColor,
  BxBadgeComponent,
  BxBadgeSize,
  BxIcon,
  BxTooltipDirective,
  alertCircleIcon,
  checkIcon,
  hourglass01Icon,
  injectBxIcons,
  minusIcon,
} from '@beyonnex/bx-angular-ui';
import { CommonTranslateService } from '../../services/common-translate/common-translate.service';
import { PRODUCT_SEVERITY, isProductSeverity } from '../../services/property-status/property-status.service.models';
import type { ProductSeverity } from '../../services/property-status/property-status.service.models';
import { isNullOrUndefined } from '../../utils/utils';

@Component({
  selector: 'bxi-product-status-badge',
  imports: [BxBadgeComponent, BxTooltipDirective],
  templateUrl: './product-status-badge.component.html',
  host: { class: 'block w-fit' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductStatusBadgeComponent {
  private readonly commonTranslate = inject(CommonTranslateService);

  /** Absent means the property does not serve this product, which renders as the grey dash. */
  severity = input.required<ProductSeverity | undefined>();
  size = input<BxBadgeSize>('sm');
  message = input<string>();
  iconOnly = input<boolean>(false);

  // A severity outside the mapped range leaves the badge without an icon or colour, so resolve to
  // undefined and let the template skip it entirely rather than render a broken badge.
  protected readonly data = computed(() => {
    const severity = this.severity();
    if (isNullOrUndefined(severity)) {
      return NOT_SERVED;
    }

    return isProductSeverity(severity) ? { icon: SEVERITY_ICON[severity], color: SEVERITY_COLOR[severity] } : undefined;
  });

  protected readonly label = computed(() => this.commonTranslate.translateProductSeverity(this.severity()));
  protected readonly tooltipHeading = computed(() => this.message() || this.label());

  constructor() {
    injectBxIcons([alertCircleIcon, hourglass01Icon, checkIcon, minusIcon]);
  }
}

const NOT_SERVED: { icon: BxIcon; color: BxBadgeColor } = { icon: 'minus', color: 'gray-blue' };

const SEVERITY_ICON: Record<ProductSeverity, BxIcon> = {
  [PRODUCT_SEVERITY.CRITICAL]: 'alert-circle',
  [PRODUCT_SEVERITY.PENDING]: 'hourglass-01',
  [PRODUCT_SEVERITY.OK]: 'check',
};

const SEVERITY_COLOR: Record<ProductSeverity, BxBadgeColor> = {
  [PRODUCT_SEVERITY.CRITICAL]: 'error',
  [PRODUCT_SEVERITY.PENDING]: 'indigo',
  [PRODUCT_SEVERITY.OK]: 'success',
};
